import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DatePicker, TimePicker, Tabs, Button, message } from 'antd';
import Select from 'react-select';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from "feather-icons-react";
import { usePatient } from '../../hooks/usePatients';
import { createEmptyVisit, examTypeOptions, paymentTypeOptions, specialtyOptions } from '../../models/visit.model';
import { visitApi } from '../../api/visit.api';
import { visitTypeApi } from '../../api/visitType.api';
import doctorApi from '../../api/doctor.api';
import patientApi from '../../api/patient.api';
import moment from 'moment';

const { TabPane } = Tabs;

const VisitForm = () => {
    const { patientId, visitId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!visitId;

    // Hooks - only fetch patient if patientId exists in URL
    const { data: patient } = usePatient(patientId || '');
    const [loading, setLoading] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    const [visitTypes, setVisitTypes] = useState([]);
    const [existingVisit, setExistingVisit] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    
    // Load doctors and visit types on component mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [doctorList, visitTypeList] = await Promise.all([
                    doctorApi.getAllDoctors(true),
                    visitTypeApi.getAllActiveVisitTypes()
                ]);
                setDoctors(doctorList);
                setVisitTypes(visitTypeList);
            } catch (error) {
                console.error('Failed to load initial data:', error);
                message.error('Не успяхме да заредим необходимите данни');
            }
        };
        loadInitialData();
    }, []);
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);
    
    // Search patients function (debounced)
    const searchPatients = (searchQuery) => {
        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        if (!searchQuery || searchQuery.length < 2) {
            setPatients([]);
            return;
        }
        
        // Set new timeout for debouncing
        const timeoutId = setTimeout(async () => {
            try {
                const results = await patientApi.searchPatients(searchQuery);
                setPatients(results);
            } catch (error) {
                console.error('Failed to search patients:', error);
                message.error('Не успяхме да търсим в пациентите');
            }
        }, 300); // 300ms debounce
        
        setSearchTimeout(timeoutId);
    };
    
    // Load existing visit if editing
    useEffect(() => {
        if (isEditing && visitId) {
            const loadVisit = async () => {
                try {
                    setLoading(true);
                    const visit = await visitApi.getVisitById(parseInt(visitId));
                    setExistingVisit(visit);
                    
                    // Load patient data for the visit
                    if (visit.patientId) {
                        try {
                            const patientData = await patientApi.getPatientById(visit.patientId);
                            setSelectedPatient(patientData);
                        } catch (error) {
                            console.error('Failed to load patient for visit:', error);
                        }
                    }
                } catch (error) {
                    console.error('Failed to load visit:', error);
                    message.error('Не успяхме да заредим данните за посещението');
                } finally {
                    setLoading(false);
                }
            };
            loadVisit();
        }
    }, [isEditing, visitId]);

    // Form state
    const [formData, setFormData] = useState(createEmptyVisit());
    const [activeTab, setActiveTab] = useState('1');

    // Initialize form data
    useEffect(() => {
        if (isEditing && existingVisit) {
            // Transform backend data to frontend format
            const transformedData = {
                ...createEmptyVisit(),
                noAl: existingVisit.noAl,
                visitDate: existingVisit.visitDate,
                visitTime: existingVisit.visitTime,
                paymentType: existingVisit.paymentType,
                patientId: existingVisit.patientId.toString(),
                doctor: {
                    isSubstitute: false, // Backend doesn't have this field yet
                    uinNumber: existingVisit.doctorUinNumber || '',
                    fullName: existingVisit.doctorName || ''
                },
                disadvantaged: existingVisit.disadvantaged,
                incidentally: existingVisit.incidentally,
                anamnesis: existingVisit.anamnesis || '',
                physicalExam: existingVisit.physicalExam || '',
                examinations: existingVisit.examinations || '',
                examType: existingVisit.examType,
                
                // Add diagnosis data
                mainDiagnosis: {
                    name: existingVisit.mainDiagnosisName || '',
                    mkbCode: existingVisit.mainDiagnosisMkbCode || ''
                },
                additionalDiagnoses: existingVisit.additionalDiagnoses 
                    ? (typeof existingVisit.additionalDiagnoses === 'string' 
                        ? JSON.parse(existingVisit.additionalDiagnoses) 
                        : existingVisit.additionalDiagnoses)
                    : [],
                
                therapy: {
                    nonReimbursable: existingVisit.nonReimbursableTherapy || '',
                    reimbursableDrugs: existingVisit.reimbursableDrugs 
                        ? (typeof existingVisit.reimbursableDrugs === 'string' 
                            ? JSON.parse(existingVisit.reimbursableDrugs) 
                            : existingVisit.reimbursableDrugs)
                        : []
                },
                visitTypeCode: existingVisit.visitTypeCode || 'STANDARD',
                documents: {
                    sickLeave: [],
                    consultations: [],
                    diagnosticProcedures: [],
                    prescriptions: [],
                    telkForm: existingVisit.telkForm,
                    notification: existingVisit.notification,
                    epicrisis: existingVisit.epicrisis,
                    medicalNote: existingVisit.medicalNote
                },
                signed: existingVisit.signed
            };
            setFormData(transformedData);
        } else if (patientId) {
            // Specific patient from URL
            setFormData(prev => ({ ...prev, patientId }));
        }
    }, [existingVisit, patientId, isEditing]);
    
    // Set selected patient when patient data is loaded or when patient is selected from dropdown
    useEffect(() => {
        if (patient) {
            setSelectedPatient(patient);
        }
    }, [patient]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    // Handle nested object changes
    const handleNestedChange = (parent, index, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: prev[parent].map((item, i) => 
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    // Add new item to array
    const addArrayItem = (arrayField, emptyItem) => {
        if (arrayField.includes('.')) {
            const [parent, child] = arrayField.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: [...(prev[parent][child] || []), emptyItem]
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [arrayField]: [...(prev[arrayField] || []), emptyItem]
            }));
        }
    };

    // Remove item from array
    const removeArrayItem = (arrayField, index) => {
        if (arrayField.includes('.')) {
            const [parent, child] = arrayField.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: (prev[parent][child] || []).filter((_, i) => i !== index)
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [arrayField]: (prev[arrayField] || []).filter((_, i) => i !== index)
            }));
        }
    };

    // Transform form data to API format
    const transformFormDataToAPI = (data) => {
        // Find doctor by UIN
        const selectedDoctor = doctors.find(doc => doc.uinNumber === data.doctor.uinNumber);
        if (!selectedDoctor) {
            throw new Error('Моля изберете валиден лекар');
        }
        
        const apiData = {
            noAl: data.noAl,
            visitDate: data.visitDate,
            visitTime: data.visitTime,
            paymentType: data.paymentType,
            patientId: parseInt(data.patientId),
            doctorId: selectedDoctor.id,
            disadvantaged: data.disadvantaged || false,
            incidentally: data.incidentally || false,
            anamnesis: data.anamnesis || '',
            physicalExam: data.physicalExam || '',
            examinations: data.examinations || '',
            examType: data.examType || 11,
            
            // Add diagnosis fields
            mainDiagnosisName: data.mainDiagnosis?.name || '',
            mainDiagnosisMkbCode: data.mainDiagnosis?.mkbCode || '',
            additionalDiagnoses: JSON.stringify(data.additionalDiagnoses || []),
            
            nonReimbursableTherapy: data.therapy?.nonReimbursable || '',
            
            // Add reimbursable drugs (prescriptions)
            reimbursableDrugs: JSON.stringify(data.therapy?.reimbursableDrugs || []),
            
            signed: data.signed || false,
            
            // Visit type - simplified approach
            visitTypeCode: data.visitTypeCode || 'STANDARD',
            
            // Document flags
            telkForm: data.documents?.telkForm || false,
            notification: data.documents?.notification || false,
            epicrisis: data.documents?.epicrisis || false,
            medicalNote: data.documents?.medicalNote || false
        };
        
        return apiData;
    };
    
    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            
            // Validate required fields
            if (!formData.noAl || !formData.visitDate || !formData.visitTime) {
                message.error('Моля попълнете всички задължителни полета');
                return;
            }
            
            // Additional validation for general visit form (when no patientId in URL)
            if (!patientId && !selectedPatient) {
                message.error('Моля изберете пациент');
                return;
            }
            
            if (!formData.doctor.uinNumber) {
                message.error('Моля изберете лекар');
                return;
            }
            
            // Validate anamnesis and physical exam (required fields)
            if (!formData.anamnesis) {
                message.error('Моля попълнете анамнезата');
                setActiveTab('2');
                return;
            }
            
            if (!formData.physicalExam) {
                message.error('Моля попълнете физикалния преглед');
                setActiveTab('2');
                return;
            }
            
            // Validate main diagnosis (required fields)
            if (!formData.mainDiagnosis.mkbCode || !formData.mainDiagnosis.name) {
                message.error('Моля попълнете основната диагноза');
                setActiveTab('3');
                return;
            }
            
            const apiData = transformFormDataToAPI(formData);
            
            if (isEditing) {
                await visitApi.updateVisit(parseInt(visitId), apiData);
                message.success('Посещението е актуализирано успешно');
            } else {
                await visitApi.createVisit(apiData);
                message.success('Посещението е създадено успешно');
            }
            // Navigate based on context
            if (patientId) {
                navigate(`/patients/${patientId}/visits`);
            } else {
                navigate('/visits');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            message.error(`Грешка: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <Sidebar id="menu-item4" id1="menu-items4" activeClassName="visit-form" />
            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/visits">Прегледи</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">
                                        Нов преглед
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Patient Selection or Info Card */}
                    {!patientId ? (
                        // Patient selection dropdown when no specific patient in URL
                        <div className="row mb-3">
                            <div className="col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="mb-3">Избери пациент и лекар</h5>
                                        
                                        {/* Patient Selection */}
                                        <div className="form-group">
                                            <label>Пациент <span className="login-danger">*</span></label>
                                            <Select
                                                value={selectedPatient ? {
                                                    value: selectedPatient.id,
                                                    label: `${selectedPatient.fullName || selectedPatient.givenName + ' ' + selectedPatient.surName + ' ' + selectedPatient.familyName} (ЕГН: ${selectedPatient.egn})`
                                                } : null}
                                                onChange={(selected) => {
                                                    const patient = patients.find(p => p.id === selected?.value);
                                                    setSelectedPatient(patient);
                                                    setFormData(prev => ({ ...prev, patientId: patient?.id.toString() || '' }));
                                                }}
                                                onInputChange={(inputValue, actionMeta) => {
                                                    if (actionMeta.action === 'input-change') {
                                                        searchPatients(inputValue);
                                                    }
                                                }}
                                                options={patients.map(patient => ({
                                                    value: patient.id,
                                                    label: `${patient.fullName || patient.givenName + ' ' + patient.surName + ' ' + patient.familyName} (ЕГН: ${patient.egn})`
                                                }))}
                                                placeholder="Започнете да въвеждате име или ЕГН на пациент..."
                                                noOptionsMessage={({ inputValue }) => 
                                                    inputValue && inputValue.length >= 2 
                                                        ? "Няма намерени пациенти" 
                                                        : "Въведете поне 2 символа за търсене"
                                                }
                                                loadingMessage={() => "Търсене..."}
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                isSearchable={true}
                                                styles={{
                                                    menuPortal: (base) => ({ 
                                                        ...base, 
                                                        zIndex: 1050 
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        marginTop: 0
                                                    })
                                                }}
                                            />
                                        </div>

                                        {/* Doctor Selection */}
                                        <div className="form-group mt-3">
                                            <label>Лекар <span className="login-danger">*</span></label>
                                            <Select
                                                value={doctors.find(doc => doc.uinNumber === formData.doctor.uinNumber) ? {
                                                    value: formData.doctor.uinNumber,
                                                    label: `${formData.doctor.fullName} (${formData.doctor.uinNumber})`
                                                } : null}
                                                onChange={(selected) => {
                                                    const selectedDoctor = doctors.find(doc => doc.uinNumber === selected?.value);
                                                    if (selectedDoctor) {
                                                        handleInputChange('doctor.uinNumber', selectedDoctor.uinNumber);
                                                        handleInputChange('doctor.fullName', selectedDoctor.fullName);
                                                    }
                                                }}
                                                options={doctors.map(doctor => ({
                                                    value: doctor.uinNumber,
                                                    label: `${doctor.fullName} (${doctor.uinNumber})`
                                                }))}
                                                placeholder="Изберете лекар..."
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    menuPortal: (base) => ({ 
                                                        ...base, 
                                                        zIndex: 1050 
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        marginTop: 0
                                                    })
                                                }}
                                            />
                                        </div>
                                        {selectedPatient && (
                                            <div className="mt-3 p-3 bg-light rounded">
                                                <h6 className="mb-1">
                                                    {selectedPatient.fullName || `${selectedPatient.givenName} ${selectedPatient.surName} ${selectedPatient.familyName}`}
                                                </h6>
                                                <p className="mb-0 text-muted">
                                                    ЕГН: {selectedPatient.egn} | Възраст: {moment().diff(selectedPatient.dateOfBirth, 'years')} г.
                                                    {selectedPatient.rzok && ` | РЗОК: ${selectedPatient.rzok}`}
                                                    {selectedPatient.zdrRajon && ` | Здр. район: ${selectedPatient.zdrRajon}`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : patient ? (
                        // Patient info display when specific patient from URL
                        <div className="row mb-3">
                            <div className="col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <h5 className="mb-1">
                                            {patient.fullName?.given || patient.givenName} {patient.fullName?.sur || patient.surName} {patient.fullName?.family || patient.familyName}
                                        </h5>
                                        <p className="mb-0 text-muted">
                                            ЕГН: {patient.egn} | Възраст: {moment().diff(patient.dateOfBirth, 'years')} г.
                                            {patient.rzok && ` | РЗОК: ${patient.rzok}`}
                                            {patient.zdrRajon && ` | Здр. район: ${patient.zdrRajon}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Visit Form */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                                            {/* Tab 1: Visit Information */}
                                            <TabPane tab="Информация за посещението" key="1">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>Основни данни за посещението</h4>
                                                        </div>
                                                    </div>

                                                    {/* Visit Number */}
                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>№ Амбулаторен лист <span className="login-danger">*</span></label>
                                                            <input
                                                                className="form-control"
                                                                type="text"
                                                                placeholder="Въведете номер"
                                                                value={formData.noAl}
                                                                onChange={(e) => handleInputChange('noAl', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Visit Date */}
                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>Дата на посещение <span className="login-danger">*</span></label>
                                                            <DatePicker
                                                                className="form-control"
                                                                value={formData.visitDate ? moment(formData.visitDate) : null}
                                                                onChange={(date, dateString) => handleInputChange('visitDate', dateString)}
                                                                placeholder="Изберете дата"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Visit Time */}
                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>Час на посещение <span className="login-danger">*</span></label>
                                                            <TimePicker
                                                                className="form-control"
                                                                value={formData.visitTime ? moment(formData.visitTime, 'HH:mm:ss') : null}
                                                                onChange={(time, timeString) => handleInputChange('visitTime', timeString + ':00')}
                                                                placeholder="Изберете час"
                                                                format="HH:mm"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Payment Type */}
                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>Тип плащане <span className="login-danger">*</span></label>
                                                            <Select
                                                                value={paymentTypeOptions.find(opt => opt.value === formData.paymentType)}
                                                                onChange={(selected) => handleInputChange('paymentType', selected?.value || 1)}
                                                                options={paymentTypeOptions}
                                                                placeholder="Изберете тип плащане"
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    menuPortal: (base) => ({ 
                                                                        ...base, 
                                                                        zIndex: 1050 
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        marginTop: 0
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Exam Type */}
                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>Тип преглед <span className="login-danger">*</span></label>
                                                            <Select
                                                                value={examTypeOptions.find(opt => opt.value === formData.examType)}
                                                                onChange={(selected) => handleInputChange('examType', selected?.value || 11)}
                                                                options={examTypeOptions}
                                                                placeholder="Изберете тип преглед"
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    menuPortal: (base) => ({ 
                                                                        ...base, 
                                                                        zIndex: 1050 
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        marginTop: 0
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                    </div>


                                                    {/* Visit Type Selection */}
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>Тип посещение</h4>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6 col-xl-4">
                                                        <div className="form-group local-forms">
                                                            <label>Тип посещение <span className="login-danger">*</span></label>
                                                            <Select
                                                                value={visitTypes.length > 0 && formData.visitTypeCode ? {
                                                                    value: formData.visitTypeCode,
                                                                    label: visitTypes.find(vt => vt.typeCode === formData.visitTypeCode)?.typeName
                                                                } : null}
                                                                onChange={(selected) => handleInputChange('visitTypeCode', selected?.value || 'STANDARD')}
                                                                options={visitTypes.map(visitType => ({
                                                                    value: visitType.typeCode,
                                                                    label: visitType.typeName
                                                                }))}
                                                                placeholder="Изберете тип посещение..."
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    menuPortal: (base) => ({ 
                                                                        ...base, 
                                                                        zIndex: 1050 
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        marginTop: 0
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row mt-3">
                                                    <div className="col-12">
                                                        <Button 
                                                            type="primary" 
                                                            onClick={() => setActiveTab('2')}
                                                            className="me-2"
                                                        >
                                                            Напред
                                                        </Button>
                                                    </div>
                                                </div>
                                            </TabPane>

                                            {/* Tab 2: Clinical Data */}
                                            <TabPane tab="Клинични данни" key="2">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>Клиничен преглед</h4>
                                                        </div>
                                                    </div>

                                                    {/* Anamnesis */}
                                                    <div className="col-12">
                                                        <div className="form-group local-forms">
                                                            <label>Анамнеза <span className="login-danger">*</span></label>
                                                            <textarea
                                                                className="form-control"
                                                                rows={4}
                                                                placeholder="Въведете анамнеза..."
                                                                value={formData.anamnesis}
                                                                onChange={(e) => handleInputChange('anamnesis', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Physical Examination */}
                                                    <div className="col-12">
                                                        <div className="form-group local-forms">
                                                            <label>Физикален преглед <span className="login-danger">*</span></label>
                                                            <textarea
                                                                className="form-control"
                                                                rows={6}
                                                                placeholder="Въведете резултати от физикалния преглед..."
                                                                value={formData.physicalExam}
                                                                onChange={(e) => handleInputChange('physicalExam', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Examinations */}
                                                    <div className="col-12">
                                                        <div className="form-group local-forms">
                                                            <label>Изследвания</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows={4}
                                                                placeholder="Въведете резултати от изследвания (ЕКГ, лабораторни, рентген и др.)..."
                                                                value={formData.examinations || ''}
                                                                onChange={(e) => handleInputChange('examinations', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row mt-3">
                                                    <div className="col-12">
                                                        <Button 
                                                            onClick={() => setActiveTab('1')}
                                                            className="me-2"
                                                        >
                                                            Назад
                                                        </Button>
                                                        <Button 
                                                            type="primary" 
                                                            onClick={() => setActiveTab('3')}
                                                            className="me-2"
                                                        >
                                                            Напред
                                                        </Button>
                                                    </div>
                                                </div>
                                            </TabPane>

                                            {/* Tab 3: Diagnoses */}
                                            <TabPane tab="Диагнози" key="3">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>Основна диагноза</h4>
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group local-forms">
                                                            <label>МКБ код <span className="login-danger">*</span></label>
                                                            <input
                                                                className="form-control"
                                                                type="text"
                                                                placeholder="Например: B34.1"
                                                                value={formData.mainDiagnosis.mkbCode}
                                                                onChange={(e) => handleInputChange('mainDiagnosis.mkbCode', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <div className="form-group local-forms">
                                                            <label>Наименование на диагнозата <span className="login-danger">*</span></label>
                                                            <input
                                                                className="form-control"
                                                                type="text"
                                                                placeholder="Въведете диагноза"
                                                                value={formData.mainDiagnosis.name}
                                                                onChange={(e) => handleInputChange('mainDiagnosis.name', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Additional Diagnoses */}
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>
                                                                Допълнителни диагнози
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    className="ms-2"
                                                                    onClick={() => addArrayItem('additionalDiagnoses', { name: '', mkbCode: '' })}
                                                                >
                                                                    <FeatherIcon icon="plus" size={14} />
                                                                    Добави
                                                                </Button>
                                                            </h4>
                                                        </div>
                                                    </div>

                                                    {formData.additionalDiagnoses.map((diagnosis, index) => (
                                                        <div key={index} className="col-12">
                                                            <div className="row align-items-end">
                                                                <div className="col-md-5">
                                                                    <div className="form-group local-forms">
                                                                        <label>МКБ код</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="text"
                                                                            placeholder="Например: M54.1"
                                                                            value={diagnosis.mkbCode}
                                                                            onChange={(e) => handleNestedChange('additionalDiagnoses', index, 'mkbCode', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="form-group local-forms">
                                                                        <label>Наименование</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="text"
                                                                            placeholder="Въведете диагноза"
                                                                            value={diagnosis.name}
                                                                            onChange={(e) => handleNestedChange('additionalDiagnoses', index, 'name', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-1">
                                                                    <div className="form-group local-forms">
                                                                        <label>&nbsp;</label>
                                                                        <Button
                                                                            danger
                                                                            onClick={() => removeArrayItem('additionalDiagnoses', index)}
                                                                            className="form-control d-flex align-items-center justify-content-center"
                                                                        >
                                                                            <FeatherIcon icon="trash-2" size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="row mt-3">
                                                    <div className="col-12">
                                                        <Button 
                                                            onClick={() => setActiveTab('2')}
                                                            className="me-2"
                                                        >
                                                            Назад
                                                        </Button>
                                                        <Button 
                                                            type="primary" 
                                                            onClick={() => setActiveTab('4')}
                                                            className="me-2"
                                                        >
                                                            Напред
                                                        </Button>
                                                    </div>
                                                </div>
                                            </TabPane>

                                            {/* Tab 4: Therapy */}
                                            <TabPane tab="Терапия" key="4">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>Терапевтично лечение</h4>
                                                        </div>
                                                    </div>

                                                    {/* Non-reimbursable therapy */}
                                                    <div className="col-12">
                                                        <div className="form-group local-forms">
                                                            <label>Общи терапевтични указания</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows={4}
                                                                placeholder="Въведете общи указания за лечение..."
                                                                value={formData.therapy.nonReimbursable || ''}
                                                                onChange={(e) => handleInputChange('therapy.nonReimbursable', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Reimbursable drugs */}
                                                    <div className="col-12">
                                                        <div className="form-heading">
                                                            <h4>
                                                                Рецептурни медикаменти (НЗОК)
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    className="ms-2"
                                                                    onClick={() => addArrayItem('therapy.reimbursableDrugs', { drugCode: '', mkbCode: '', quantity: 0, days: 0 })}
                                                                >
                                                                    <FeatherIcon icon="plus" size={14} />
                                                                    Добави медикамент
                                                                </Button>
                                                            </h4>
                                                        </div>
                                                    </div>

                                                    {(formData.therapy.reimbursableDrugs || []).map((drug, index) => (
                                                        <div key={index} className="col-12">
                                                            <div className="row align-items-end">
                                                                <div className="col-md-3">
                                                                    <div className="form-group local-forms">
                                                                        <label>Код на лекарство</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="text"
                                                                            placeholder="Например: NG028"
                                                                            value={drug.drugCode}
                                                                            onChange={(e) => {
                                                                                const newDrugs = [...(formData.therapy.reimbursableDrugs || [])];
                                                                                newDrugs[index] = { ...newDrugs[index], drugCode: e.target.value };
                                                                                handleInputChange('therapy.reimbursableDrugs', newDrugs);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <div className="form-group local-forms">
                                                                        <label>МКБ код</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="text"
                                                                            placeholder="F20.0"
                                                                            value={drug.mkbCode}
                                                                            onChange={(e) => {
                                                                                const newDrugs = [...(formData.therapy.reimbursableDrugs || [])];
                                                                                newDrugs[index] = { ...newDrugs[index], mkbCode: e.target.value };
                                                                                handleInputChange('therapy.reimbursableDrugs', newDrugs);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-2">
                                                                    <div className="form-group local-forms">
                                                                        <label>Количество</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="number"
                                                                            value={drug.quantity}
                                                                            onChange={(e) => {
                                                                                const newDrugs = [...(formData.therapy.reimbursableDrugs || [])];
                                                                                newDrugs[index] = { ...newDrugs[index], quantity: parseInt(e.target.value) || 0 };
                                                                                handleInputChange('therapy.reimbursableDrugs', newDrugs);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-2">
                                                                    <div className="form-group local-forms">
                                                                        <label>Дни</label>
                                                                        <input
                                                                            className="form-control"
                                                                            type="number"
                                                                            value={drug.days}
                                                                            onChange={(e) => {
                                                                                const newDrugs = [...(formData.therapy.reimbursableDrugs || [])];
                                                                                newDrugs[index] = { ...newDrugs[index], days: parseInt(e.target.value) || 0 };
                                                                                handleInputChange('therapy.reimbursableDrugs', newDrugs);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-2">
                                                                    <div className="form-group local-forms">
                                                                        <label>&nbsp;</label>
                                                                        <Button
                                                                            danger
                                                                            onClick={() => {
                                                                                const newDrugs = (formData.therapy.reimbursableDrugs || []).filter((_, i) => i !== index);
                                                                                handleInputChange('therapy.reimbursableDrugs', newDrugs);
                                                                            }}
                                                                            className="form-control d-flex align-items-center justify-content-center"
                                                                        >
                                                                            <FeatherIcon icon="trash-2" size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="row mt-3">
                                                    <div className="col-12 d-flex justify-content-between align-items-start flex-wrap">
                                                        <Button 
                                                            onClick={() => setActiveTab('3')}
                                                            className="me-2 mb-2"
                                                        >
                                                            Назад
                                                        </Button>
                                                        
                                                        {/* Form Buttons - responsive layout */}
                                                        <div className="doctor-submit d-flex align-items-center flex-column flex-sm-row">
                                                            <Link
                                                                to={patientId ? `/patients/${patientId}/visits` : '/visits'}
                                                                className="btn btn-primary cancel-form d-flex align-items-center justify-content-center mb-2 mb-sm-0 me-sm-2"
                                                                style={{ 
                                                                    height: '45px', 
                                                                    textAlign: 'center', 
                                                                    textDecoration: 'none',
                                                                    paddingLeft: '15px',
                                                                    paddingRight: '15px',
                                                                    width: '100%',
                                                                    maxWidth: '150px'
                                                                }}
                                                            >
                                                                Отказ
                                                            </Link>
                                                            <Button
                                                                type="primary"
                                                                htmlType="submit"
                                                                loading={loading}
                                                                className="btn btn-primary submit-form"
                                                                style={{ 
                                                                    height: '45px',
                                                                    width: '100%',
                                                                    maxWidth: '200px'
                                                                }}
                                                            >
                                                                {isEditing ? 'Запази промените' : 'Създай посещение'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TabPane>
                                        </Tabs>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VisitForm;